import clsx from 'clsx';
import React, { forwardRef } from 'react';
import { sanitizers } from '@/utils/formValidation/sanitizers';

type TextFieldProps = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: 'text' | 'email' | 'tel' | 'number' | 'password' | 'date';
  size?: 'default' | 'small';
  error?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  maxLength?: number;
  inputMode?: 'text' | 'email' | 'tel' | 'numeric';
  pattern?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  inputRef?: React.RefObject<HTMLInputElement>;
  className?: string;
  helperText?: string;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
};

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      value,
      onChange,
      placeholder,
      type = 'text',
      size = 'default',
      error = false,
      disabled = false,
      autoFocus = false,
      maxLength,
      inputMode,
      pattern,
      onFocus,
      onBlur,
      inputRef,
      className,
      helperText,
      prefix,
      suffix,
    },
    ref
  ) => {
    const inputRefToUse = inputRef || ref;
    const isNumeric = type === 'number' || inputMode === 'numeric';
    const isSmall = size === 'small';

    const wrapperClassName = helperText
      ? clsx('w-full p-2 bg-[#F4F4F4] rounded-lg flex flex-col gap-1', error && 'outline outline-[#CC4747]')
      : 'contents';

    const inputWrapperClassName = clsx(
      'rounded-lg px-4 py-3 flex items-center justify-start gap-4',
      helperText
        ? [
            'w-full bg-white outline outline-[#767676]',
            error && 'outline outline-1 -outline-offset-1 outline-[#CC4747]',
          ]
        : [
            isSmall ? 'w-30 flex-none' : 'w-full min-w-0 flex-1',
            disabled ? 'bg-[#E5E5E5] cursor-not-allowed' : 'bg-[#F4F4F4]',
            error && 'outline outline-1 -outline-offset-1 outline-[#CC4747]',
          ]
    );

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isNumeric) {
        const sanitized = sanitizers.numeric(e.target.value);
        onChange(sanitized);
      } else {
        onChange(e.target.value);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent +, -, e, E keys for numeric fields (scientific notation and signs)
      if (isNumeric && (e.key === '+' || e.key === '-' || e.key === 'e' || e.key === 'E')) {
        e.preventDefault();
      }
    };

    return (
      <div className={clsx(wrapperClassName, className)}>
        <div className={inputWrapperClassName}>
          {prefix}
          <input
            ref={inputRefToUse}
            type={type === 'number' ? 'text' : type}
            value={value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            autoFocus={autoFocus}
            maxLength={maxLength}
            inputMode={inputMode}
            pattern={pattern}
            className={clsx(
              'bg-transparent border-none outline-none text-sm font-medium placeholder:text-[#B2B2B2] flex-1 basis-0 min-w-0',
              disabled ? 'text-[#999999] cursor-not-allowed' : value ? 'text-black' : 'text-[#B2B2B2]'
            )}
          />
          {suffix}
        </div>
        {helperText && (
          <div className="self-stretch px-2 inline-flex items-center">
            <span className="text-black text-xs">{helperText}</span>
          </div>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';
